import {
  MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX,
  type TargetRatio,
} from "@nexus/contracts";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Skeleton,
  Textarea,
  cn,
} from "@nexus/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import {
  adminOrderPartnerOptionsQueryOptions,
  adminOrderScriptAdPlatformsQueryOptions,
} from "@/lib/queries";

import { VendorIdentity } from "../../components/orders-list/VendorIdentity";
import { TargetRatiosPicker } from "../../components/TargetRatiosPicker";
import { useTargetRatioSelection } from "../../components/use-target-ratio-selection";
import type { OrderPartnerOption } from "../../types/marketplace.types";

export interface SendToPartnerSubmission {
  tenantVendorId: string;
  handoffOverrideNote?: string;
  targetRatios?: TargetRatio[];
  emailClient: boolean;
  emailPartner: boolean;
}

/** Copies are not the client's order (AD-3276), so the client stays off unless ops opt in. */
const DEFAULT_EMAIL_CLIENT = false;
const DEFAULT_EMAIL_PARTNER = true;

/** The source order was saved without target ratios, so the copy needs the admin to pick them. */
export function isMissingTargetRatios(order: {
  handoff?: { targetRatios: readonly TargetRatio[] } | null;
}): boolean {
  return (order.handoff?.targetRatios.length ?? 0) === 0;
}

interface SendToPartnerDialogProps {
  orderId: string;
  orderScriptId: string;
  clientName: string;
  scriptTitle: string;
  /** The source order has no stored target ratios, so the admin must pick them for the copy. */
  needsTargetRatios?: boolean;
  /** A previous send of this line that never succeeded; reopening must resubmit it unchanged to replay. */
  initialSubmission?: SendToPartnerSubmission;
  pending: boolean;
  /**
   * Opened from a menu item that is gone by close time, so focus would land on
   * the page body and the next Tab would dismiss the non-modal order panel.
   */
  returnFocusTo?: HTMLElement | null;
  onSubmit: (submission: SendToPartnerSubmission) => void;
  onClose: () => void;
}

export function SendToPartnerDialog({
  orderId,
  orderScriptId,
  clientName,
  scriptTitle,
  needsTargetRatios = false,
  initialSubmission,
  pending,
  returnFocusTo,
  onSubmit,
  onClose,
}: SendToPartnerDialogProps) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSubmission?.tenantVendorId ?? null,
  );
  const [note, setNote] = useState(initialSubmission?.handoffOverrideNote ?? "");
  const [emailClient, setEmailClient] = useState(
    initialSubmission?.emailClient ?? DEFAULT_EMAIL_CLIENT,
  );
  const [emailPartner, setEmailPartner] = useState(
    initialSubmission?.emailPartner ?? DEFAULT_EMAIL_PARTNER,
  );
  const adPlatformsQuery = useQuery({
    ...adminOrderScriptAdPlatformsQueryOptions(orderId, orderScriptId),
    enabled: needsTargetRatios,
  });
  const ratioSelection = useTargetRatioSelection({
    open: needsTargetRatios,
    adPlatforms: adPlatformsQuery.data ?? null,
    platformsReady:
      !adPlatformsQuery.isLoading &&
      (adPlatformsQuery.isError || adPlatformsQuery.data !== undefined),
    initialSelected: initialSubmission?.targetRatios,
  });
  const hasRequiredRatios = !needsTargetRatios || ratioSelection.selected.length > 0;
  const partnersQuery = useQuery(adminOrderPartnerOptionsQueryOptions(orderId));
  const partners = partnersQuery.data ?? [];
  // A restored partner may have disconnected or stopped receiving orders since the failed send.
  const sendableId = partners.some(
    (partner) => partner.tenantVendorId === selectedId && partner.canReceiveOrders,
  )
    ? selectedId
    : null;
  const isPartnerListCurrent = partnersQuery.isSuccess && !partnersQuery.isFetching;

  const handleSubmit = () => {
    if (!sendableId || !isPartnerListCurrent || !hasRequiredRatios) return;
    const trimmedNote = note.trim();
    onSubmit({
      tenantVendorId: sendableId,
      ...(trimmedNote ? { handoffOverrideNote: trimmedNote } : {}),
      ...(needsTargetRatios ? { targetRatios: ratioSelection.selected } : {}),
      emailClient,
      emailPartner,
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* A client with many partners must not push the email choices and Send off-screen. */}
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto"
        onCloseAutoFocus={(event) => {
          if (!returnFocusTo?.isConnected) return;
          event.preventDefault();
          returnFocusTo.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t("marketplace.orders.sendToPartner.title", "Send to another partner")}
          </DialogTitle>
          <DialogDescription>
            {t("marketplace.orders.sendToPartner.description", {
              title: scriptTitle,
              client: clientName,
              defaultValue:
                "A copy of “{{title}}” goes to the partner you pick as a new {{client}} order.",
            })}
          </DialogDescription>
        </DialogHeader>
        <PartnerChoices
          loading={partnersQuery.isLoading}
          failed={partnersQuery.isError}
          partners={partners}
          selectedId={sendableId}
          onSelect={setSelectedId}
        />
        {needsTargetRatios ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">
              {t(
                "marketplace.orders.sendToPartner.ratiosHint",
                "This order has no target ratios saved. Pick the cuts the new partner should deliver.",
              )}
            </p>
            <TargetRatiosPicker
              family={ratioSelection.family}
              selected={ratioSelection.selected}
              onToggle={ratioSelection.toggle}
              isReady={ratioSelection.isReady}
              showEmptyError={false}
              disabled={pending}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="send-to-partner-note">
            {t("marketplace.orders.sendToPartner.noteLabel", "Note for the partner (optional)")}
          </Label>
          <Textarea
            id="send-to-partner-note"
            value={note}
            maxLength={MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
          />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-foreground">
            {t("marketplace.orders.sendToPartner.emailsLabel", "Emails")}
          </legend>
          <EmailToggle
            id="send-to-partner-email-partner"
            checked={emailPartner}
            disabled={pending}
            onChange={setEmailPartner}
            label={t("marketplace.orders.sendToPartner.emailPartner", "Email partner")}
            hint={
              emailPartner
                ? t(
                    "marketplace.orders.sendToPartner.emailPartnerOnHint",
                    "The partner gets the handoff email.",
                  )
                : t(
                    "marketplace.orders.sendToPartner.emailPartnerOffHint",
                    "No handoff email goes out. Contact the partner yourself.",
                  )
            }
          />
          <EmailToggle
            id="send-to-partner-email-client"
            checked={emailClient}
            disabled={pending}
            onChange={setEmailClient}
            label={t("marketplace.orders.sendToPartner.emailClient", "Email client")}
            hint={clientEmailHint(t, { emailClient, emailPartner })}
          />
        </fieldset>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            disabled={!sendableId || !isPartnerListCurrent || !hasRequiredRatios || pending}
            onClick={handleSubmit}
          >
            {t("marketplace.orders.sendToPartner.submit", "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Partner off skips the whole handoff, Cc included, and a client with no orders
 * inbox falls back to the person placing the order: the ops admin, not the client.
 */
function clientEmailHint(
  t: TFunction,
  { emailClient, emailPartner }: { emailClient: boolean; emailPartner: boolean },
): string {
  if (!emailClient) {
    return t(
      "marketplace.orders.sendToPartner.emailClientOffHint",
      "The client isn't emailed about this order.",
    );
  }
  return emailPartner
    ? t(
        "marketplace.orders.sendToPartner.emailClientOnHint",
        "The client's orders inbox is copied on the handoff and later emails. If none is set, you're copied instead.",
      )
    : t(
        "marketplace.orders.sendToPartner.emailClientOnNoHandoffHint",
        "The client's orders inbox is copied on later emails, but there's no handoff email. If none is set, you're copied instead.",
      );
}

function EmailToggle({
  id,
  checked,
  disabled,
  onChange,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onChange(value === true)}
        aria-describedby={`${id}-hint`}
        className="mt-0.5"
      />
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={id}>{label}</Label>
        <span id={`${id}-hint`} className="text-caption text-muted-foreground">
          {hint}
        </span>
      </div>
    </div>
  );
}

function PartnerChoices({
  loading,
  failed,
  partners,
  selectedId,
  onSelect,
}: {
  loading: boolean;
  failed: boolean;
  partners: OrderPartnerOption[];
  selectedId: string | null;
  onSelect: (tenantVendorId: string) => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  }
  if (failed) {
    return (
      <p role="alert" className="text-sm text-error">
        {t("marketplace.orders.sendToPartner.loadError", "Couldn't load this client's partners.")}
      </p>
    );
  }
  if (partners.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(
          "marketplace.orders.sendToPartner.empty",
          "This client isn't connected to any other partner yet.",
        )}
      </p>
    );
  }
  return (
    <div
      role="radiogroup"
      aria-label={t("marketplace.orders.sendToPartner.partnerLabel", "Partner")}
      className="flex max-h-64 flex-col gap-2 overflow-y-auto p-0.5"
    >
      {partners.map((partner) => {
        const isSelected = partner.tenantVendorId === selectedId;
        return (
          <button
            key={partner.tenantVendorId}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!partner.canReceiveOrders}
            onClick={() => onSelect(partner.tenantVendorId)}
            className={cn(
              "flex min-h-11 items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors duration-150 ease-out motion-reduce:transition-none",
              isSelected
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted/40",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            <VendorIdentity name={partner.vendor.name} logoUrl={partner.vendor.logoUrl} />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-foreground">
                {partner.vendor.name}
              </span>
              {partner.canReceiveOrders ? null : (
                <span className="text-caption text-muted-foreground">
                  {t(
                    "marketplace.orders.sendToPartner.unavailable",
                    "Can't receive orders right now",
                  )}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
